import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  saveClientConflictCheckToClient,
  saveConflictCheckResults,
  searchConflictCheck,
} from "api";
import ClientComboBox from "components/common/ClientComboBox";
import MattersCombobox from "pages/Postings/MattersComboBox";
import { Label } from "@progress/kendo-react-labels";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Tile } from "components/UIComponents/Tile";
import { Loader } from "@progress/kendo-react-indicators"; // Import your loader component
import { toast } from "react-toastify";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Input } from "@progress/kendo-react-inputs";
import { FaSearch } from "react-icons/fa";
import SubmitButton from "components/common/SubmitButton";
import styles from "./Casefile.module.scss";

const FIELD_MIN_LENGTH = {
  firstName: 3,
  lastName: 3,
  companyName: 3,
  matterDetails: 4,
};

const isBelowMinLength = (value, minLength) => {
  const length = (value ?? "").trim().length;
  return length > 0 && length < minLength;
};

const getMinLengthError = (minLength) =>
  `minimum ${minLength} characters are required`;

const ConflictCheck = ({
  fromCaseFile,
  fromClientScreen,
  onClose,
  onSaved,
  moveNext,
  formData,
  matterId,
  currentMatterDetails,
  setHandlefn,
}) => {
  const DEFAULT_STATE = {
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    postcode: "",
    companyName: "",
    companyRegNo: "",
    matterDetails: "",
  };

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [inputState, setInputState] = useState(DEFAULT_STATE);
  const [saveTarget, setSaveTarget] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [isSavingClient, setIsSavingClient] = useState(false);
  const [isSavingMatter, setIsSavingMatter] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);

  const matter_Id = fromCaseFile ? matterId : formData?.matterDetails?.matterId;

  const matter_details = fromCaseFile
    ? currentMatterDetails
    : formData?.matterDetails;

  const handleInputChange = (value, label) => {
    setInputState((prevState) => ({
      ...prevState,
      [label]: value,
    }));
  };

  const saveAndSearch = async (startNew) => {
    try {
      setLoading(true);
      await saveConflictCheckResults(matter_Id, inputState);
      onSaved?.();
      if (startNew) {
        setData([]);
        setInputState(DEFAULT_STATE);
        setValidationAttempted(false);
      } else {
        onClose();
      }
      toast.success("Search Saved");
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const saveToClient = async () => {
    if (!selectedClient?.clientId || isSavingClient) return;
    try {
      setIsSavingClient(true);
      await saveClientConflictCheckToClient(selectedClient.clientId, inputState);
      onSaved?.();
      setSaveTarget(null);
      setSelectedClient(null);
      onClose();
      toast.success("Search Saved");
    } catch (error) {
    } finally {
      setIsSavingClient(false);
    }
  };

  const saveToMatter = async () => {
    if (!selectedMatter?.matterId || isSavingMatter) return;
    try {
      setIsSavingMatter(true);
      await saveConflictCheckResults(selectedMatter.matterId, inputState);
      onSaved?.();
      setSaveTarget(null);
      setSelectedMatter(null);
      onClose();
      toast.success("Search Saved");
    } catch (error) {
    } finally {
      setIsSavingMatter(false);
    }
  };

  const closeSaveClientDialog = () => {
    if (isSavingClient) return;
    setSaveTarget(null);
    setSelectedClient(null);
  };

  const closeSaveMatterDialog = () => {
    if (isSavingMatter) return;
    setSaveTarget(null);
    setSelectedMatter(null);
  };

  const handleNext = useCallback(async () => {
    try {
      if (data) await saveConflictCheckResults(matter_Id, inputState);
    } catch (error) {
      console.error("Failed to save conflict check results", error);
    } finally {
      moveNext({}, { conflictCheckData: data });
    }
  }, [moveNext, data, matter_Id, inputState]);

  const clientSubType =
    formData?.clientData?.clientSubType || currentMatterDetails?.client?.type;
  const isPerson =
    fromClientScreen ||
    clientSubType?.toLowerCase() === "individual" ||
    clientSubType?.toLowerCase() === "person";
  const isCompany =
    fromClientScreen || clientSubType?.toLowerCase() === "company";
  const isPersonActive =
    !fromClientScreen ||
    !(inputState.companyName ||
      inputState.companyRegNo ||
      inputState.postcode);
  const isCompanyActive =
    !fromClientScreen ||
    !(inputState.firstName ||
      inputState.lastName ||
      inputState.dateOfBirth);

  const fieldErrors = useMemo(
    () => ({
      firstName:
        isPerson &&
        isPersonActive &&
        isBelowMinLength(inputState.firstName, FIELD_MIN_LENGTH.firstName),
      lastName:
        isPerson &&
        isPersonActive &&
        isBelowMinLength(inputState.lastName, FIELD_MIN_LENGTH.lastName),
      companyName:
        isCompany &&
        isCompanyActive &&
        isBelowMinLength(inputState.companyName, FIELD_MIN_LENGTH.companyName),
      matterDetails: isBelowMinLength(
        inputState.matterDetails,
        FIELD_MIN_LENGTH.matterDetails,
      ),
    }),
    [
      isPerson,
      isCompany,
      isPersonActive,
      isCompanyActive,
      inputState.firstName,
      inputState.lastName,
      inputState.companyName,
      inputState.matterDetails,
    ],
  );

  const hasValidationErrors = Object.values(fieldErrors).some(Boolean);

  const hasSearchableValue = useMemo(() => {
    if (fromClientScreen) {
      return (
        inputState.firstName ||
        inputState.lastName ||
        inputState.dateOfBirth ||
        inputState.postcode ||
        inputState.companyName ||
        inputState.companyRegNo ||
        inputState.matterDetails
      );
    }
    if (isPerson) {
      return (
        inputState.firstName || inputState.lastName || inputState.dateOfBirth
      );
    }
    if (isCompany) {
      return (
        inputState.companyName ||
        inputState.companyRegNo ||
        inputState.postcode
      );
    }
    return false;
  }, [fromClientScreen, isPerson, isCompany, inputState]);

  const handleSearch = async () => {
    setValidationAttempted(true);
    if (hasValidationErrors) return;
    try {
      setLoading(true);
      const response = await searchConflictCheck(inputState);
      setData(response?.data?.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (setHandlefn) {
      setHandlefn(() => handleNext);
      return () => setHandlefn && setHandlefn(null);
    }
  }, [setHandlefn, handleNext]);

  const showFieldError = (field) =>
    validationAttempted && fieldErrors[field];

  const content = (
    <div>
      {loading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 9999,
          }}
        >
          <Loader themeColor="warning" size="large" type="pulsing" />
        </div>
      )}
      {!fromClientScreen && (
        <div
          className={`${styles["sub-text"]} m-3 ms-2 mt-1 p-3 ps-4 pe-4 pt-2 pb-0 overflow-auto`}
        >
          <i>
            {matter_details?.client?.clientName} - {matter_details?.matterRef} -{" "}
            {matter_details?.details}{" "}
            {matter_details?.appCode && (
              <span> - [{matter_details?.appCode}]</span>
            )}{" "}
          </i>
        </div>
      )}
      <div
        className={`${setHandlefn && "bg-white m-3 shadow rounded-3 px-2 py-3"
          }`}
      >
        <Tile
          height={fromClientScreen ? "320px" : showFieldError("firstName") || showFieldError("lastName") || showFieldError("companyName") || showFieldError("matterDetails") ? "245px" : "220px"}
          heading="Search"
          headingClass=""
          className={`${setHandlefn ? "bg-transparent shadow-none" : "m-3"
            }  mt-3 p-3 ps-4 pe-4 pt-2 pb-0 overflow-auto`}
        >
          <div className={`${setHandlefn ? styles["item-container"] : "row"}`}>
            {isPerson && (
              <>
                <div
                  className={`${setHandlefn ? "d-flex flex-column" : "col-4"}`}
                >
                  <Label className="label d-block mb-1">First Name</Label>
                  <div className={showFieldError("firstName") ? "validation-invalid" : ""}>
                    <Input
                      style={{ width: "260px", height: "32px" }}
                      placeholder="First Name"
                      disabled={!isPersonActive}
                      onChange={(e) => handleInputChange(e.value, "firstName")}
                      value={inputState["firstName"]}
                    />
                  </div>
                  {showFieldError("firstName") && (
                    <div className="fs-sm my-1 text-danger">
                      {getMinLengthError(FIELD_MIN_LENGTH.firstName)}
                    </div>
                  )}
                </div>
                <div
                  className={`${setHandlefn ? "d-flex flex-column" : "col-4"}`}
                >
                  <Label className="label d-block mb-1">Last Name</Label>
                  <div className={showFieldError("lastName") ? "validation-invalid" : ""}>
                    <Input
                      style={{ width: "260px", height: "32px" }}
                      placeholder="Last Name"
                      disabled={!isPersonActive}
                      onChange={(e) => handleInputChange(e.value, "lastName")}
                      value={inputState["lastName"]}
                    />
                  </div>
                  {showFieldError("lastName") && (
                    <div className="fs-sm my-1 text-danger">
                      {getMinLengthError(FIELD_MIN_LENGTH.lastName)}
                    </div>
                  )}
                </div>
                <div
                  className={`${setHandlefn ? "d-flex flex-column" : "col-4"
                    } form-date-picker note-date-picker check-date-picker`}
                >
                  <Label className="label d-block mb-1">Date Of Birth</Label>
                  <DatePicker
                    style={{ width: "260px" }}
                    placeholder="Select Date"
                    disabled={!isPersonActive}
                    toggleButton={CalenderCustomToggleButton}
                    onChange={(e) => handleInputChange(e.value, "dateOfBirth")}
                    value={inputState["dateOfBirth"]}
                  />
                </div>
              </>
            )}

            {isCompany && (
              <>
                <div
                  className={`${setHandlefn ? "d-flex flex-column" : "col-4"} ${fromClientScreen ? "mt-3" : ''}`}
                >
                  <Label className="label d-block mb-1">Company Name</Label>
                  <div className={showFieldError("companyName") ? "validation-invalid" : ""}>
                    <Input
                      style={{ width: "260px", height: "32px" }}
                      placeholder="Company Name"
                      disabled={!isCompanyActive}
                      onChange={(e) => handleInputChange(e.value, "companyName")}
                      value={inputState["companyName"]}
                    />
                  </div>
                  {showFieldError("companyName") && (
                    <div className="fs-sm my-1 text-danger">
                      {getMinLengthError(FIELD_MIN_LENGTH.companyName)}
                    </div>
                  )}
                </div>
                <div
                  className={`${setHandlefn ? "d-flex flex-column" : "col-4"} ${fromClientScreen ? "mt-3" : ''}`}
                >
                  <Label className="label d-block mb-1">
                    Company Registration No
                  </Label>
                  <Input
                    style={{ width: "260px", height: "32px" }}
                    placeholder="Reg No"
                    disabled={!isCompanyActive}
                    onChange={(e) => handleInputChange(e.value, "companyRegNo")}
                    value={inputState["companyRegNo"]}
                  />
                </div>
                <div
                  className={`${setHandlefn ? "d-flex flex-column" : "col-4"} ${fromClientScreen ? "mt-3" : ''}`}
                >
                  <Label className="label d-block mb-1">Post Code</Label>
                  <Input
                    style={{ width: "260px", height: "32px" }}
                    placeholder="Post Code"
                    disabled={!isCompanyActive}
                    onChange={(e) => handleInputChange(e.value, "postcode")}
                    value={inputState.postcode}
                  />
                </div>
              </>
            )}
          </div>
          <div
            className={`${setHandlefn ? "d-flex flex-column gap-2 flex-xl-row" : "row"
              } mt-3`}
          >
            <div
              className={
                setHandlefn ? styles["matter-details-container"] : "col-8"
              }
            >
              <Label className="label d-block mb-1">Matter Details</Label>
              <div className={showFieldError("matterDetails") ? "validation-invalid" : ""}>
                <Input
                  id="payer"
                  style={{
                    maxWidth: "100%",
                    height: "32px",
                  }}
                  placeholder="Matter Details"
                  onChange={(e) => handleInputChange(e.value, "matterDetails")}
                  value={inputState["matterDetails"]}
                ></Input>
              </div>
              {showFieldError("matterDetails") && (
                <div className="fs-sm my-1 text-danger">
                  {getMinLengthError(FIELD_MIN_LENGTH.matterDetails)}
                </div>
              )}
            </div>
            <div
              className={`
                ${setHandlefn ? "col-12 col-xl-4 " : "col-4"
                } d-flex justify-content-end mt-3`}
            >
              <Button
                className="common-btn"
                onClick={handleSearch}
                disabled={!hasSearchableValue || loading}
              >
                <div className="d-flex gap-2 align-items-center">
                  <FaSearch color="white" />
                  <div>Search</div>
                </div>
              </Button>
            </div>
          </div>
        </Tile>
        {data && (
          <Tile
            heading="Search Results"
            headingClass=""
            className={`${setHandlefn ? "bg-transparent shadow-none" : "m-3"
              }  mt-1  px-4 py-2 overflow-auto`}
          >
            <Grid data={data} style={{ height: "200px" }} className="pb-1">
              <GridColumn
                field="matched"
                title="Matched"
                className="ps-4 fs-sm"
                headerClassName="ps-4"
                width={30}
                editor="text"
              />
              <GridColumn
                field="details"
                title="Details"
                width={200}
                className="fs-sm"
                editable={false}
              />
              {/* <GridColumn width="60px" cell={RedirectCell} /> */}
            </Grid>
          </Tile>
        )}
      </div>
      {fromCaseFile && (
        <div className="dialog-footer">
          <Button className="common-btn bgBtnSecondary me-2" onClick={onClose}>
            Close
          </Button>

          <Button
            className="common-btn me-2"
            onClick={() => saveAndSearch(true)}
          >
            Save & New Search
          </Button>

          <Button
            className="common-btn  me-3"
            onClick={() => saveAndSearch(false)}
          >
            Save & Close
          </Button>
        </div>
      )}
      {fromClientScreen && (
        <div className="dialog-footer">
          <Button className="common-btn bgBtnSecondary me-2" onClick={onClose}>
            Close
          </Button>

          <Button
            className="common-btn me-2"
            disabled={!data}
            onClick={() => setSaveTarget("client")}
          >
            Save in Client Doc
          </Button>

          <Button
            className="common-btn me-3"
            disabled={!data}
            onClick={() => setSaveTarget("matter")}
          >
            Save in Casefile
          </Button>
        </div>
      )}
      {saveTarget === "client" && (
        <Dialog
          title="Select Client"
          onClose={closeSaveClientDialog}
          width={420}
          className="dialog-sm">
          <div className="p-3 ps-4 pe-4">
            <Label className="label d-block mb-2">Client</Label>
            <ClientComboBox
              onChangeClient={(client) => setSelectedClient(client)}
              val={selectedClient}
              width="100%"
            />
          </div>
          <DialogActionsBar>
            <div className="dialog-footer">
              <Button
                className="common-btn bgBtnSecondary me-2"
                onClick={closeSaveClientDialog}
                disabled={isSavingClient}>
                Cancel
              </Button>
              <SubmitButton
                type="button"
                className="me-3"
                onClick={saveToClient}
                disabled={!selectedClient?.clientId}
                isLoading={isSavingClient}>
                Save
              </SubmitButton>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
      {saveTarget === "matter" && (
        <Dialog
          title="Select Matter"
          onClose={closeSaveMatterDialog}
          width={420}
          className="dialog-sm">
          <div className="p-3 ps-4 pe-4">
            <Label className="label d-block mb-2">Matter</Label>
            <MattersCombobox
              value={selectedMatter}
              onChangematter={(e) => setSelectedMatter(e.value)}
              style={{ width: "100%", height: "32px" }}
            />
          </div>
          <DialogActionsBar>
            <div className="dialog-footer">
              <Button
                className="common-btn bgBtnSecondary me-2"
                onClick={closeSaveMatterDialog}
                disabled={isSavingMatter}>
                Cancel
              </Button>
              <SubmitButton
                type="button"
                className="me-3"
                onClick={saveToMatter}
                disabled={!selectedMatter?.matterId}
                isLoading={isSavingMatter}>
                Save
              </SubmitButton>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );

  return formData ? (
    <Tile
      heading="Conflict Check"
      className="px-4 h-100 overflow-auto bg-transparent border-0 border w-100 bg-danger shadow-none "
    >
      <div style={{ height: "calc(100% - 110px)" }}>
        <div className="overflow-auto" style={{ height: "calc(100% - 55px)" }}>
          <div className={`${!setHandlefn && styles["onboarding_container"]}`}>
            {content}
          </div>
        </div>
        <div
          className={`${!setHandlefn && styles["onboarding_container"]
            } d-flex flex-column`}
          style={{ height: "55px" }}
        >
          <div className="d-flex justify-content-end mt-2">
            <Button
              className="common-btn bgBtnSecondary me-2"
              onClick={() => saveAndSearch(true)}
            >
              Save & New Search
            </Button>
          </div>
          <hr className="my-2 w-100" />
        </div>
      </div>
    </Tile>
  ) : (
    <div>{content}</div>
  );
};

export default ConflictCheck;
